import { FC } from 'react';
import { Col, Divider, Row, Typography } from 'antd';
import { TbFileDescription } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { BaseSkeleton } from '@/shared/components/skeletons';

import { FinancialOverviewLeftSectionProps } from '../../types';
import { FinancialOverviewIdentitySkeleton } from '../FinancialOverviewSkeletons';

export const FinancialOverviewLeftSection: FC<FinancialOverviewLeftSectionProps> = ({
    banner,
    brandColors,
    contentHeight = 'auto',
    description,
    details,
    holdings,
    loading,
    ready,
    showAbout = true,
    showHoldings = true,
}) => {
    const { token } = useAntDesignTheme();
    const shouldRenderAbout = showAbout && (!!description || loading);
    const shouldRenderHoldings = showHoldings && !!holdings;
    const bannerPalette = getFinancialOverviewBannerPalette(brandColors);
    const bannerBackground = bannerPalette.length
        ? createFinancialOverviewBannerGradient(bannerPalette)
        : `linear-gradient(135deg, ${token.colorFillSecondary} 0%, ${token.colorBgContainer} 100%)`;

    return (
        <>
            <div style={{ minHeight: contentHeight === 'auto' ? undefined : contentHeight }}>
                {ready && !loading ? (
                    <>
                        <div
                            data-testid='financial-overview-brand-banner'
                            style={{
                                background: bannerBackground,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                height: '12rem',
                                margin: '1rem',
                                overflow: 'hidden',
                            }}
                        />
                        {banner}
                        <div style={{ padding: '0 1rem 1.25rem' }}>{details}</div>
                    </>
                ) : (
                    <FinancialOverviewIdentitySkeleton />
                )}
            </div>
            {(shouldRenderAbout || shouldRenderHoldings) && (
                <>
                    <Divider orientation='right' style={{ margin: '1.125rem 0' }} />
                    <Row gutter={[24, 24]}>
                        {shouldRenderAbout && (
                            <Col xs={24} lg={shouldRenderHoldings ? 12 : 24}>
                                {description && !loading ? (
                                    <>
                                        <Typography.Title level={4}>
                                            <TbFileDescription
                                                style={{ marginRight: '0.375rem', verticalAlign: '-0.15rem' }}
                                            />
                                            About the Company
                                        </Typography.Title>
                                        <Typography.Paragraph ellipsis={{ rows: 6, expandable: true, symbol: 'More' }}>
                                            {description}
                                        </Typography.Paragraph>
                                    </>
                                ) : (
                                    <BaseSkeleton paragraph={{ rows: 8 }} />
                                )}
                            </Col>
                        )}
                        {shouldRenderHoldings && (
                            <Col xs={24} lg={shouldRenderAbout ? 12 : 24}>
                                {holdings}
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </>
    );
};

const normalizeBrandHexColor = (color: string) => {
    const normalizedColor = color.trim().toLocaleLowerCase();
    const shorthandMatch = /^#([\da-f])([\da-f])([\da-f])$/i.exec(normalizedColor);

    if (shorthandMatch) {
        return `#${shorthandMatch[1]}${shorthandMatch[1]}${shorthandMatch[2]}${shorthandMatch[2]}${shorthandMatch[3]}${shorthandMatch[3]}`;
    }

    return /^#[\da-f]{6}$/i.test(normalizedColor) ? normalizedColor : undefined;
};

const getBrandColorChroma = (color: string) => {
    const channels = [color.slice(1, 3), color.slice(3, 5), color.slice(5, 7)].map((channel) =>
        Number.parseInt(channel, 16),
    );

    return Math.max(...channels) - Math.min(...channels);
};

const getFinancialOverviewBannerPalette = (colors?: string[]) => {
    const normalizedColors = [
        ...new Set((colors ?? []).map(normalizeBrandHexColor).filter((color): color is string => color !== undefined)),
    ];
    const chromaticColors = normalizedColors.filter((color) => getBrandColorChroma(color) >= 24);
    const orderedColors = chromaticColors.length
        ? [...chromaticColors, ...normalizedColors.filter((color) => !chromaticColors.includes(color))]
        : normalizedColors;

    return orderedColors.slice(0, 3);
};

const createFinancialOverviewBannerGradient = (palette: string[]) => {
    const [primary, secondary = primary, tertiary = secondary] = palette;

    return `linear-gradient(135deg, ${primary} 0%, ${secondary} 55%, ${tertiary} 100%)`;
};
